import React, { useEffect, useRef, useState } from 'react';
import { InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useUpdateUserManufacturerDiscountMutation } from 'src/api/v1/manufacturer-discount-ApiSlice';
import { ProfileManufacturer } from 'src/pages/ProfileSettingsPage/components/ProfileManufacturerAutocomplete';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';

const SAVE_DELAY_MS = 500;
const BLOCKED_KEYS = ['ArrowUp', 'ArrowDown'];

const clampPercent = (value: number) => Math.min(Math.max(value, 0), 100);

type Props = {
    manufacturer: ProfileManufacturer;
    discount: number;
};

export const ManufacturerDiscountRow = ({ manufacturer, discount }: Props) => {
    const dispatch = useAppDispatch();
    const [updateUserManufacturerDiscount] = useUpdateUserManufacturerDiscountMutation();
    const [value, setValue] = useState(discount);
    const savedValue = useRef(discount);
    const pendingSave = useRef<{ timeout: ReturnType<typeof setTimeout>, send: () => void } | null>(null);

    useEffect(() => {
        setValue(discount);
        savedValue.current = discount;
    }, [discount]);

    // flush a pending save when the page is left within the delay
    useEffect(() => () => {
        if (pendingSave.current) {
            clearTimeout(pendingSave.current.timeout);
            pendingSave.current.send();
        }
    }, []);

    const handleChange = (rawValue: string) => {
        const nextValue = clampPercent(Number(rawValue) || 0);
        setValue(nextValue);

        const send = () => {
            pendingSave.current = null;
            updateUserManufacturerDiscount({
                internal_product_manufacturer_id: manufacturer.internal_product_manufacturer_id,
                user_manufacturer_id: manufacturer.user_manufacturer_id,
                discount_percent: nextValue,
            })
                .unwrap()
                .then(() => {
                    savedValue.current = nextValue;
                })
                .catch(() => {
                    setValue(savedValue.current);
                    dispatch(showSnackBar({
                        text: ts('something_went_wrong_try_again', 'Something went wrong, please try again.'),
                        severity: 'error',
                    }));
                });
        };

        if (pendingSave.current) clearTimeout(pendingSave.current.timeout);
        pendingSave.current = { timeout: setTimeout(send, SAVE_DELAY_MS), send };
    };

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="body2">
                {ts('q_manu_disc_def', 'Default discount for {manufacturer}', [
                    { template: '{manufacturer}', value: manufacturer.manufacturer_name },
                ])}
            </Typography>
            <TextField
                type="number"
                size="small"
                value={value}
                sx={{ width: 120, flexShrink: 0 }}
                slotProps={{
                    htmlInput: { min: 0, max: 100 },
                    input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                }}
                onChange={(event) => handleChange(event.target.value)}
                onFocus={(event) => event.target.select()}
                onKeyDown={(event) => {
                    if (BLOCKED_KEYS.includes(event.key)) event.preventDefault();
                }}
                onWheel={(event) => (event.target as HTMLInputElement).blur()}
            />
        </Stack>
    );
};
